import { z } from 'zod';

// Enums
export const SmsActionMessageType = z.enum(['FOLLOWUP_SMS', 'MISSED_CALL_SMS']);
export type SmsActionMessageType = z.infer<typeof SmsActionMessageType>;

export const SmsActionCampaignMode = z.enum(['INCLUDE', 'EXCLUDE']);
export type SmsActionCampaignMode = z.infer<typeof SmsActionCampaignMode>;

export const SmsActionType = z.enum(['LLM_CONFIRMATION']);
export type SmsActionType = z.infer<typeof SmsActionType>;

export const SmsActionLogStatus = z.enum([
  'PENDING',
  'REPLIED',
  'TIMED_OUT',
  'ACTION_SUCCESS',
  'ACTION_FAILED',
  'ERROR',
]);
export type SmsActionLogStatus = z.infer<typeof SmsActionLogStatus>;

export const SmsActionResultOutcome = z.enum(['CONFIRMED', 'NOT_CONFIRMED', 'TIMED_OUT', 'ERROR']);
export type SmsActionResultOutcome = z.infer<typeof SmsActionResultOutcome>;

// Result handler config
export const resultHandlerConfigSchema = z.object({
  type: z.string(),
  on_outcomes: z.array(z.string()),
  params: z.record(z.string(), z.unknown()).default({}),
});

export type ResultHandlerConfig = z.infer<typeof resultHandlerConfigSchema>;

// Config schema
export const smsActionConfigSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(2),
  is_active: z.boolean().default(true),
  message_type: SmsActionMessageType,
  campaign_mode: SmsActionCampaignMode,
  action_type: SmsActionType,
  action_params: z.record(z.string(), z.unknown()),
  on_timeout_action_type: SmsActionType.nullable().optional(),
  on_timeout_action_params: z.record(z.string(), z.unknown()).nullable().optional(),
  reply_window_minutes: z.number().int().min(1).max(1440).default(10),
  notification_emails: z.array(z.string().email()).default([]),
  on_result_handlers: z.array(resultHandlerConfigSchema).default([]),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
  campaigns: z
    .array(
      z.object({
        id: z.string().uuid(),
        config_id: z.string().uuid(),
        campaign_id: z.string().uuid(),
      }),
    )
    .optional(),
});

export type SmsActionConfig = z.infer<typeof smsActionConfigSchema>;

export const createSmsActionConfigSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  is_active: z.boolean().default(true),
  message_type: SmsActionMessageType,
  campaign_mode: SmsActionCampaignMode,
  action_type: SmsActionType,
  action_params: z.record(z.string(), z.unknown()),
  on_timeout_action_type: SmsActionType.nullable().optional(),
  on_timeout_action_params: z.record(z.string(), z.unknown()).nullable().optional(),
  reply_window_minutes: z.number().int().min(1).max(1440).default(10),
  notification_emails: z.array(z.string().email()).default([]),
  on_result_handlers: z.array(resultHandlerConfigSchema).default([]),
  campaign_ids: z.array(z.string().uuid()).default([]),
});

export type CreateSmsActionConfigDto = z.infer<typeof createSmsActionConfigSchema>;

export const updateSmsActionConfigSchema = createSmsActionConfigSchema.partial();

export type UpdateSmsActionConfigDto = z.infer<typeof updateSmsActionConfigSchema>;

// Log schema
export const smsActionLogSchema = z.object({
  id: z.string().uuid(),
  config_id: z.string().uuid(),
  campaign_id: z.string().uuid().nullable(),
  contact_id: z.string().uuid().nullable(),
  contact_phone: z.string(),
  message_type: SmsActionMessageType,
  outbound_message: z.string().nullable(),
  inbound_message: z.string().nullable(),
  status: SmsActionLogStatus,
  sent_at: z.date().or(z.string().datetime()),
  reply_deadline: z.date().or(z.string().datetime()),
  replied_at: z.date().or(z.string().datetime()).nullable(),
  action_result: z.record(z.string(), z.unknown()).nullable(),
});

export type SmsActionLog = z.infer<typeof smsActionLogSchema>;

// Result schema
export const smsActionResultSchema = z.object({
  id: z.string().uuid(),
  log_id: z.string().uuid(),
  config_id: z.string().uuid(),
  campaign_id: z.string().uuid().nullable(),
  contact_id: z.string().uuid().nullable(),
  contact_phone: z.string(),
  action_type: SmsActionType,
  outcome: SmsActionResultOutcome,
  details: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.date().or(z.string().datetime()),
});

export type SmsActionResult = z.infer<typeof smsActionResultSchema>;
